<template>
  <div class="w-full lg:w-3/4 mx-auto px-3 lg:px-8">
    <div class="header mt-4">
      <zt-button color="dark-400" icon="angle-left" text class="go-back" @click="goBack"
        data-vs-button="certificates-panel-btn-goback">
        {{ $t('message.certificates.goBack') }}
      </zt-button>
      <h2 class="font-medium text-2xl">
        {{ $t("message.certificates.title") }}
      </h2>
    </div>
    <div class="w-full mt-10">
      <div class="mb-4">
        <h3>{{ $t("message.certificates.sections.all") }}</h3>
      </div>
      <div class="w-full lg:w-1/3">
        <div class="bg-white flex rounded justify-start shadow-md hover:shadow-lg cursor-pointer p-8"
          @click="openDialog(true)" data-vs-link="certificates-panel-link-opendialog">
          <div class="mr-4">
            <zt-icon name="file-text-outline" :style="{ fontSize: '2rem' }" />
          </div>
          <div class="flex-grow">
            <p class="font-medium text-xl mb-2 text-zt-dark-600 porlet-title">
              {{ $t("message.certificates.cards.all.title") }}
            </p>
            <span class="text-zt-dark-300">{{ $t("message.certificates.cards.all.subtitle") }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full my-10" v-if="hasSectionHistory">
      <div class="mb-4">
        <h3>{{ $t("message.certificates.sections.requested") }}</h3>
      </div>
      <div v-if="isMobile" class="w-full bg-white rounded shadow-md border overflow">
        <div class="px-6 py-4 font-bold border-b bg-zt-white">
          <h2 class="text-zt-xl text-zt-dark-600">
            {{ $t('message.certificates.table.title') }}
          </h2>
        </div>
        <div class="p-3">
          <div v-for="(row, index) in rows" :key="index" class="flex flex-col p-2 border rounded"
            :class="{ 'mb-4': index !== rows.length - 1 }">
            <div class="flex justify-between items-center mb-2 p-3">
              <div class="w-1/2">{{ row.created_at }}</div>
              <div class="w-1/2 flex justify-end items-center">
                <zt-button outlined @click="downloadHistory(row)" class="button-table-action"
                  :style="{ width: '125px' }" data-vs-button="certificates-panel-btn-downloadhistoryrow">
                  <div class="flex items-center">
                    <zt-icon name="box-arrow-down" class="mr-2" />
                    <div>{{ $t('message.certificates.table.download') }}</div>
                  </div>
                </zt-button>
              </div>
            </div>
            <div class="flex items-center mb-2 p-3">
              <zt-icon name="file-text-outline" class="mr-2" style="font-size: 2rem;" />
              <span>{{ row.name_certificate }}</span>
            </div>
            <div class="flex justify-start items-center p-3">
              <zt-avatar :text="row.name" color="secondary-100" />
              <span class="ml-2 flex flex-wrap">{{ row.name }}</span>
            </div>
          </div>
        </div>
      </div>
      <zt-data-table v-else :columns="columns" :rows="rows" :config="{ pagination: { use: false } }">
        <template v-slot:header>
          <div class="px-6 py-4 font-bold border-b bg-zt-white">
            <h2 class="text-zt-xl text-zt-dark-600">
              {{ $t('message.certificates.table.title') }}
            </h2>
          </div>
        </template>
        <template v-slot:name="{ item }">
          <div class="flex justify-start items-center">
            <zt-avatar :text="item.name" color="secondary-100" />
            <span class="ml-2 flex flex-wrap">{{ item.name }}</span>
          </div>
        </template>
        <template v-slot:name_certificate="{ item }">
          <div class="flex items-center">
            <zt-icon name="file-text-outline" class="mr-2" style="font-size: 2rem;" />
            <span>{{ item.name_certificate }}</span>
          </div>
        </template>
        <template v-slot:id="{ item }">
          <div class="flex justify-center items-center">
            <zt-button outlined @click="downloadHistory(item)" class="button-table-action"
              data-vs-button="certificates-panel-btn-donwloadhistoryitem">
              <div class="flex items-center">
                <zt-icon name="box-arrow-down" class="mr-2" />
                <div>{{ $t('message.certificates.table.download') }}</div>
              </div>
            </zt-button>
          </div>
        </template>
      </zt-data-table>
    </div>
    <search-certificates-dialog :active="dialogActive" :shownFn="openDialog" :item="historyItem"
      @reload-history="getRequestedHistory" />
    <download-certificates-dialog :active="dialogDownloadActive" :shownFn="openDownloadDialog" :item="historyItem" />
  </div>
</template>

<script>
import moment from 'moment'
import Users from "@/apiClient/Users";
import SearchCertificatesDialog from './modals/SearchCertificatesDialog'
import DownloadCertificatesDialog from './modals/DownloadCertificatesDialog'
import { useWindowResize } from '@sichern/ui-elements'
export default {
  name: "CertificatesPanel",
  components: {
    DownloadCertificatesDialog,
    SearchCertificatesDialog
  },
  provide() {
    return {
      openDialog: this.openDialog,
      openDownloadDialog: this.openDownloadDialog
    }
  },
  data() {
    return {
      windowResize: useWindowResize(this),
      dialogActive: false,
      dialogDownloadActive: false,
      hasSectionHistory: false,
      columns: [
        {
          name: this.$t('message.workflow.table.columns.date'),
          dataField: 'created_at',
          width: 140
        },
        {
          name: this.$t('message.searchComponent.head_text_table_search_1'),
          dataField: 'name',
        },
        {
          name: this.$t('message.workflow.table.columns.documentType'),
          dataField: 'name_certificate',
        },
        {
          name: this.$t('message.workflow.table.columns.actions'),
          dataField: 'id',
          width: 162
        }
      ],
      rows: [],
      downloadFromHistory: false,
      historyItem: null,
      stepModal: 1
    }
  },
  computed: {
    isMobile() {
      return this.windowResize.width < 1024
    }
  },
  async created() {
    this.usersApi = new Users()
    await this.getRequestedHistory()
  },
  methods: {
    hasHistory() {
      return window.history.length > 2
    },
    goBack() {
      this.hasHistory() ? this.$router.go(-1) : this.$router.push('/')
    },
    openDialog(bool) {
      this.dialogActive = bool
    },
    openDownloadDialog(bool) {
      this.dialogDownloadActive = bool
    },
    async getRequestedHistory() {
      try {
        const { data: { data } } = await this.usersApi.getCertificateRequested()
        this.rows = data.map(
          (certificate) => ({
            ...certificate,
            ...{
              created_at: moment(certificate.created_at, 'DD/MM/YYYY HH:mm:ss.SSSSSS ZZ').format('DD/MM/YYYY'),
              name: this.$store.getters.getUser.nombre + ' ' + this.$store.getters.getUser.apellido
            }
          })
        )
        this.hasSectionHistory = data.length > 0
      } catch (e) {
        this.hasSectionHistory = false
      }
    },
    async downloadHistory(item) {
      this.historyItem = item
      this.openDownloadDialog(true)
    },
    reset() {
      this.historyItem = null
      this.downloadFromHistory = null
    }
  }
}
</script>

<style scoped lang="scss">
h2,
h3 {
  color: #707070;
}

h3 {
  font-size: 1.25rem;
}

::v-deep {
  .zt-button.go-back {
    background: none !important;
    color: #707070 !important;
    padding: 4px !important;
    margin: 0;
    font-size: 1rem !important;

    span:first-child {
      width: 10px;
    }

    span.zt-button-text {
      margin-top: 2px;
    }
  }

  .zt-data-table {

    thead,
    tbody,
    tfoot {
      background: #fff !important;
    }

    th {
      font-weight: 500 !important;
    }
  }

  .button-table-action.zt-button {
    background: #fff !important;
    color: #707070 !important;
    border-color: #707070 !important;

    .zt-icon {
      font-size: 1.5rem;
    }
  }
}
</style>